import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  useAdminUsers,
  useChangeRole,
  useDeleteUser,
  useInviteUser,
  useMe,
  useResendInvite,
  useResetPassword,
  useSetUserEnabled,
  useUpdateNotificationPreferences,
  type AdminUser,
} from '../api/hooks';
import { changeOwnPassword } from '../auth/cognito-api';
import { useConfirm } from '../components/ConfirmDialog';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../components/Toast';

const ROLE_LABEL: Record<NonNullable<AdminUser['role']>, string> = {
  'super-admin': 'super-admin',
  'tenant-admin': 'tenant-admin',
  'tenant-viewer': 'tenant-viewer',
};

// Render an ISO timestamp as a short relative label: "just now", "12m ago",
// "3h ago", "2d ago", or a locale date once it crosses a week. The full ISO
// string is exposed via the cell's `title` so admins can read the precise
// timestamp on hover. Returns null for falsy input — caller renders "Never".
function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const deltaSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (deltaSec < 45) return 'just now';
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.floor(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.floor(deltaHr / 24);
  if (deltaDay < 7) return `${deltaDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function AdminUsers() {
  const me = useMe();
  const users = useAdminUsers();
  const [inviteOpen, setInviteOpen] = useState(false);
  // DE-150 — self-service change-password modal state. Reachable from
  // the user's own row regardless of role (every signed-in user can
  // change their own password).
  const [changePwOpen, setChangePwOpen] = useState(false);

  if (me.data && me.data.role === 'tenant-viewer') {
    return (
      <div className="card max-w-md">
        <h2 className="font-semibold mb-1">Forbidden</h2>
        <p className="text-sm text-muted">
          Viewers can't manage users. Ask a tenant-admin.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted">
            {me.data?.scope === 'all'
              ? 'Every user across every tenant.'
              : `Users in your tenant (${me.data?.tenant_id}).`}
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium"
        >
          Invite user
        </button>
      </div>

      <div className="card overflow-x-auto">
        {users.isLoading && (
          <table className="w-full text-sm" aria-label="Loading users">
            <tbody>
              <Skeleton.TableRows rows={4} cols={7} />
            </tbody>
          </table>
        )}
        {users.error && (
          <p className="text-sm text-warn">{(users.error as Error).message}</p>
        )}
        {users.data && users.data.users.length === 0 && (
          <p className="text-sm text-muted">No users.</p>
        )}
        {users.data && users.data.users.length > 0 && me.data && (
          <UserTable
            users={users.data.users}
            currentSub={me.data.sub}
            callerRole={me.data.role}
            callerTenant={me.data.tenant_id}
            onChangeOwnPassword={() => setChangePwOpen(true)}
          />
        )}
      </div>

      {inviteOpen && me.data && (
        <InviteModal
          callerRole={me.data.role}
          callerTenant={me.data.tenant_id}
          onClose={() => setInviteOpen(false)}
        />
      )}

      {changePwOpen && (
        <ChangePasswordModal onClose={() => setChangePwOpen(false)} />
      )}
    </div>
  );
}

function UserTable({
  users,
  currentSub,
  callerRole,
  callerTenant,
  onChangeOwnPassword,
}: {
  users: AdminUser[];
  currentSub: string | undefined;
  callerRole: 'super-admin' | 'tenant-admin' | 'tenant-viewer';
  callerTenant: string;
  onChangeOwnPassword: () => void;
}) {
  const del = useDeleteUser();
  const notify = useUpdateNotificationPreferences();
  const changeRole = useChangeRole();
  const resetPw = useResetPassword();
  const resendInvite = useResendInvite();
  const setEnabled = useSetUserEnabled();
  const confirm = useConfirm();
  const toast = useToast();
  const allRoles: NonNullable<AdminUser['role']>[] = ['super-admin', 'tenant-admin', 'tenant-viewer'];
  // DE-149 — Notify is super-admin-only (digest emails only go to super-admin
  // anyway; toggle was informational noise for everyone else). Hide the
  // column entirely for tenant-admin / tenant-viewer; backend enforces too.
  const showNotify = callerRole === 'super-admin';
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-muted border-b border-border">
          <th className="font-medium py-2 pr-4">Email</th>
          <th className="font-medium py-2 pr-4">Tenant</th>
          <th className="font-medium py-2 pr-4">Role</th>
          <th className="font-medium py-2 pr-4">Status</th>
          <th className="font-medium py-2 pr-4">Last activity</th>
          {showNotify && <th className="font-medium py-2 pr-4">Notify</th>}
          <th className="font-medium py-2 pr-4">Created</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {users.map((u) => {
          const isSelf = u.username === currentSub;
          // DE-146 — self always; super-admin anyone; tenant-admin in own tenant; viewer none.
          const canEditNotify =
            isSelf ||
            callerRole === 'super-admin' ||
            (callerRole === 'tenant-admin' && u.tenant_id === callerTenant);
          // DE-140/141/142 — cross-user write privileges. Mirror backend rules:
          //   super-admin: anyone but self
          //   tenant-admin: own-tenant non-super-admins, not self
          //   tenant-viewer: nobody
          const canMutate =
            !isSelf &&
            (callerRole === 'super-admin' ||
              (callerRole === 'tenant-admin' &&
                u.tenant_id === callerTenant &&
                u.role !== 'super-admin'));
          // tenant-admin can grant tenant-admin/tenant-viewer only.
          const grantableRoles: NonNullable<AdminUser['role']>[] =
            callerRole === 'super-admin'
              ? allRoles
              : allRoles.filter((r) => r !== 'super-admin');
          const isInvitePending = u.status === 'FORCE_CHANGE_PASSWORD';
          return (
            <tr
              key={u.username}
              className={`border-b border-border last:border-0 transition-colors hover:bg-soft ${
                u.enabled ? '' : 'opacity-60'
              }`}
              title={u.enabled ? undefined : 'User is disabled; cannot sign in.'}
            >
              <td className="py-2 pr-4">{u.email ?? '—'}</td>
              <td className="py-2 pr-4">
                <span className="pill-muted">{u.tenant_id ?? '—'}</span>
              </td>
              <td className="py-2 pr-4">
                {canMutate && u.role ? (
                  <select
                    value={u.role}
                    disabled={changeRole.isPending}
                    onChange={async (e) => {
                      const newRole = e.target.value as NonNullable<AdminUser['role']>;
                      if (newRole === u.role) return;
                      const ok = await confirm({
                        title: 'Change role?',
                        body: (
                          <>
                            Change role of{' '}
                            <span className="font-mono">{u.email ?? u.username}</span> from{' '}
                            <span className="pill-muted">{u.role}</span> to{' '}
                            <span className="pill-muted">{newRole}</span>?
                          </>
                        ),
                        confirmLabel: 'Change role',
                      });
                      if (!ok) return;
                      changeRole.mutate(
                        { username: u.username, role: newRole },
                        {
                          onSuccess: () =>
                            toast.success(
                              `Role updated to ${newRole} for ${u.email ?? u.username}`,
                            ),
                          onError: (err) =>
                            toast.error(`Role change failed: ${(err as Error).message}`),
                        },
                      );
                    }}
                    className={`text-xs rounded px-1.5 py-0.5 border border-border bg-soft ${
                      u.role === 'super-admin' ? 'text-accent font-medium' : ''
                    }`}
                  >
                    {grantableRoles.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className={u.role === 'super-admin' ? 'pill-accent' : 'pill-muted'}>
                    {u.role ? ROLE_LABEL[u.role] : '—'}
                  </span>
                )}
              </td>
              <td className="py-2 pr-4 text-xs">
                {!u.enabled ? (
                  <span className="text-warn">DISABLED</span>
                ) : u.auth_method === 'microsoft' ? (
                  // Federated Artome users never leave FORCE_CHANGE_PASSWORD in
                  // Cognito — AdminLinkProviderForUser doesn't touch UserStatus
                  // and the password path is unreachable (PreAuth guard). The
                  // raw status is misleading; show the auth path instead.
                  <span
                    className="pill-muted"
                    title="Signs in via Microsoft 365 (Entra). No Cognito password."
                  >
                    Microsoft
                  </span>
                ) : (
                  u.status ?? '—'
                )}
              </td>
              <td className="py-2 pr-4 text-xs tabular-nums">
                {(() => {
                  // DE-231 — "activity", not "sign-in". The admin Lambda already
                  // falls back to the sign-in stamp when no activity has been
                  // recorded, so this is never emptier than the old column.
                  const rel = relativeTime(u.last_activity);
                  if (!rel) {
                    return (
                      <span
                        className="text-muted"
                        title="This user has never used the portal"
                      >
                        Never
                      </span>
                    );
                  }
                  // Keep the sign-in fact reachable without spending a second
                  // column on it — it answers a different question (last full
                  // re-authentication) that only comes up occasionally.
                  const tip = u.last_signed_in
                    ? `Last activity: ${u.last_activity}\nLast sign-in: ${u.last_signed_in}`
                    : `Last activity: ${u.last_activity}\nNo sign-in recorded`;
                  return <span title={tip}>{rel}</span>;
                })()}
              </td>
              {showNotify && (
                <td className="py-2 pr-4">
                  {u.role === 'super-admin' ? (
                    <label
                      className={`inline-flex items-center gap-1.5 text-xs ${
                        canEditNotify ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                      }`}
                      title={
                        u.notify_unassigned
                          ? 'Receiving unassigned-device digest emails. Click to opt out.'
                          : 'Opted out of unassigned-device digest emails. Click to opt in.'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={u.notify_unassigned}
                        disabled={!canEditNotify || notify.isPending}
                        onChange={(e) =>
                          notify.mutate({
                            username: u.username,
                            notify_unassigned: e.target.checked,
                          })
                        }
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-muted">
                        {u.notify_unassigned ? 'on' : 'off'}
                      </span>
                    </label>
                  ) : (
                    <span
                      className="text-xs text-muted"
                      title="The unassigned-device digest only goes to super-admins; this toggle has no effect for non-super-admin users."
                    >
                      —
                    </span>
                  )}
                </td>
              )}
              <td className="py-2 pr-4 tabular-nums text-xs">
                {u.created ? new Date(u.created).toLocaleDateString() : '—'}
              </td>
              <td className="py-2 text-right">
                {isSelf ? (
                  // DE-150 — own row gets a self-service change-password button
                  // instead of the no-op "you" label. Federated (Microsoft)
                  // users have no usable Cognito password; show a static label.
                  u.auth_method === 'microsoft' ? (
                    <span
                      className="text-xs text-muted"
                      title="You sign in with Microsoft 365; no Cognito password to change."
                    >
                      Microsoft sign-in
                    </span>
                  ) : (
                    <button
                      onClick={onChangeOwnPassword}
                      className="text-accent text-xs hover:underline"
                      title="Change your own password (current + new)"
                    >
                      Change password
                    </button>
                  )
                ) : (
                  <div className="inline-flex items-center gap-2 text-xs">
                    {/* Password actions only make sense for password-auth users.
                        Federated rows skip Resend (custom invite email already
                        points to Microsoft) and Reset (no Cognito password to
                        reset; the PreAuth guard blocks native sign-in anyway). */}
                    {canMutate && isInvitePending && u.auth_method !== 'microsoft' && (
                      <button
                        disabled={resendInvite.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Re-send invitation?',
                            body: (
                              <>
                                Send a fresh invitation email to{' '}
                                <span className="font-mono">{u.email}</span>?
                              </>
                            ),
                            confirmLabel: 'Re-send',
                          });
                          if (!ok) return;
                          resendInvite.mutate(u.username, {
                            onSuccess: () => toast.success(`Invitation re-sent to ${u.email}`),
                            onError: (err) =>
                              toast.error(`Resend failed: ${(err as Error).message}`),
                          });
                        }}
                        className="text-accent hover:underline disabled:opacity-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent rounded-sm"
                        title="Re-send the invitation email (only valid while user is on the temporary password)"
                      >
                        Resend
                      </button>
                    )}
                    {canMutate && !isInvitePending && u.auth_method !== 'microsoft' && (
                      <button
                        disabled={resetPw.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Send password-reset email?',
                            body: (
                              <>
                                <span className="font-mono">{u.email}</span> will be locked out
                                until they complete the reset.
                              </>
                            ),
                            confirmLabel: 'Send reset',
                          });
                          if (!ok) return;
                          resetPw.mutate(u.username, {
                            onSuccess: () =>
                              toast.success(`Password-reset email sent to ${u.email}`),
                            onError: (err) =>
                              toast.error(`Reset failed: ${(err as Error).message}`),
                          });
                        }}
                        className="text-accent hover:underline disabled:opacity-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent rounded-sm"
                        title="Send a password-reset email via Cognito"
                      >
                        Reset
                      </button>
                    )}
                    {canMutate && (
                      <button
                        disabled={setEnabled.isPending}
                        onClick={async () => {
                          const next = !u.enabled;
                          const ok = await confirm({
                            title: next ? 'Re-enable user?' : 'Disable user?',
                            body: next ? (
                              <>
                                Re-enable <span className="font-mono">{u.email ?? u.username}</span>?
                              </>
                            ) : (
                              <>
                                Disable <span className="font-mono">{u.email ?? u.username}</span>?
                                They will no longer be able to sign in.
                              </>
                            ),
                            confirmLabel: next ? 'Enable' : 'Disable',
                            confirmVariant: next ? 'primary' : 'danger',
                          });
                          if (!ok) return;
                          setEnabled.mutate(
                            { username: u.username, enabled: next },
                            {
                              onSuccess: () =>
                                toast.success(
                                  next
                                    ? `Enabled ${u.email ?? u.username}`
                                    : `Disabled ${u.email ?? u.username}`,
                                ),
                              onError: (err) =>
                                toast.error(`${next ? 'Enable' : 'Disable'} failed: ${(err as Error).message}`),
                            },
                          );
                        }}
                        className="text-muted hover:underline disabled:opacity-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent rounded-sm"
                        title={u.enabled ? 'Disable user (revoke sign-in without deleting)' : 'Re-enable user'}
                      >
                        {u.enabled ? 'Disable' : 'Enable'}
                      </button>
                    )}
                    {canMutate && (
                      <button
                        disabled={del.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Delete user?',
                            body: (
                              <>
                                Permanently delete{' '}
                                <span className="font-mono">{u.email ?? u.username}</span>?{' '}
                                <span className="text-warn">This cannot be undone.</span>
                              </>
                            ),
                            confirmLabel: 'Delete',
                            confirmVariant: 'danger',
                          });
                          if (!ok) return;
                          del.mutate(u.username, {
                            onSuccess: () => toast.success(`Deleted ${u.email ?? u.username}`),
                            onError: (err) =>
                              toast.error(`Delete failed: ${(err as Error).message}`),
                          });
                        }}
                        className="text-warn hover:underline disabled:opacity-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-warn rounded-sm"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function InviteModal({
  callerRole,
  callerTenant,
  onClose,
}: {
  callerRole: 'super-admin' | 'tenant-admin' | 'tenant-viewer';
  callerTenant: string;
  onClose: () => void;
}) {
  const invite = useInviteUser();
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState(callerTenant);
  const [role, setRole] = useState<NonNullable<AdminUser['role']>>('tenant-viewer');

  const isSuper = callerRole === 'super-admin';
  const availableRoles: NonNullable<AdminUser['role']>[] = isSuper
    ? ['super-admin', 'tenant-admin', 'tenant-viewer']
    : ['tenant-admin', 'tenant-viewer'];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    invite.mutate({ email, role, tenant_id: tenantId }, { onSuccess: () => onClose() });
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal">
      <form onSubmit={submit} className="card w-full max-w-md space-y-4">
        <div>
          <h2 className="font-semibold">Invite user</h2>
          <p className="text-xs text-muted">
            Artome users (@artome.fi, @artomeusa.com) sign in with Microsoft 365.
            Other users get a temporary password by email and set a permanent
            one on first sign-in. Use the Entra primary UPN
            (firstname.lastname@artome.fi) for Artome invites — not an alias.
          </p>
        </div>
        <label className="block">
          <span className="text-xs text-muted">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted">Tenant</span>
          <input
            type="text"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            required
            disabled={!isSuper}
            className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm font-mono disabled:bg-soft disabled:text-muted"
          />
          {!isSuper && (
            <span className="text-[10px] text-muted">Locked to your tenant.</span>
          )}
        </label>
        <label className="block">
          <span className="text-xs text-muted">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as NonNullable<AdminUser['role']>)}
            className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-white"
          >
            {availableRoles.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
        {invite.error && (
          <p className="text-xs text-warn">
            {(invite.error as Error).message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted hover:text-fg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={invite.isPending}
            className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium disabled:opacity-50"
          >
            {invite.isPending ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </form>
    </div>
  );
}

// DE-150 — self-service change-password modal. Calls Cognito's
// ChangePassword (current + new) directly from the browser using the
// signed-in user's access_token. Does not go through the admin Lambda
// — that uses AdminSetUserPassword, which bypasses the old-password
// check and is the wrong semantic for "user changes their own password".
function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  const [previous, setPrevious] = useState('');
  const [proposed, setProposed] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const proposedMismatch = confirm.length > 0 && proposed !== confirm;
  const canSubmit =
    !submitting &&
    !done &&
    previous.length > 0 &&
    proposed.length >= 8 &&
    proposed === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const token = auth.user?.access_token;
    if (!token) {
      setError('not signed in');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await changeOwnPassword(token, previous, proposed);
      setDone(true);
    } catch (err) {
      setError((err as Error).message ?? 'change failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal">
      <form onSubmit={submit} className="card w-full max-w-md space-y-4">
        <div>
          <h2 className="font-semibold">Change password</h2>
          <p className="text-xs text-muted">
            Cognito requires your current password to confirm. After changing,
            you stay signed in on this device — other sessions keep working
            until they expire.
          </p>
        </div>
        {done ? (
          <>
            <p className="text-sm">Password changed. ✓</p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="block">
              <span className="text-xs text-muted">Current password</span>
              <input
                type="password"
                value={previous}
                onChange={(e) => setPrevious(e.target.value)}
                required
                autoFocus
                autoComplete="current-password"
                className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-muted">New password</span>
              <input
                type="password"
                value={proposed}
                onChange={(e) => setProposed(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm"
              />
              <span className="text-[10px] text-muted">
                Cognito policy: 8+ chars, mixed case, digit.
              </span>
            </label>
            <label className="block">
              <span className="text-xs text-muted">Confirm new password</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                className="block w-full mt-1 px-3 py-2 border border-border rounded-md text-sm"
              />
              {proposedMismatch && (
                <span className="text-[10px] text-warn">Passwords do not match.</span>
              )}
            </label>
            {error && <p className="text-xs text-warn">{error}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-muted hover:text-fg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium disabled:opacity-50"
              >
                {submitting ? 'Changing…' : 'Change password'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
